import * as React from 'react';
import SearchField from '../components/SearchField';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import moment from 'moment-timezone';
import useShare from '@/src/Share/components/hooks/useShare';
import useSurveyResults from '@/src/Activations/components/hooks/useSurveyResults';
import useUserOrg from '@/src/Authentication/components/hooks/useUserOrg';
import useAuthenticatedApi from '@/src/Authentication/components/hooks/useAuthenticatedApi';
import { toast } from 'sonner';
import { dedupeShares } from '../lib/eventStats';

/*
 * Shares list v2 — the full share ledger (de-bridging the classic
 * shares tab): search, sent/unsent chips, thumbnails, delete, and
 * client-side CSV exports (shares + survey results).
 */

const PAGE = 50;
const PREVIEW_KEY = 'psv2-shares-preview';

function toCsv(rows: Record<string, any>[]) {
    if (rows.length === 0) return '';
    const keySet = new Set<string>();
    rows.forEach((r) => Object.keys(r).forEach((k) => keySet.add(k)));
    const keys = Array.from(keySet);
    const esc = (v: any) => {
        const s2 = v == null ? '' : String(v);
        return /[",\n]/.test(s2) ? `"${s2.replace(/"/g, '""')}"` : s2;
    };
    return [
        keys.join(','),
        ...rows.map((r) => keys.map((k) => esc(r[k])).join(',')),
    ].join('\n');
}

function downloadCsv(name: string, rows: Record<string, any>[]) {
    const blob = new Blob([toCsv(rows)], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = name;
    a.click();
    URL.revokeObjectURL(url);
}

export default function SharesListV2() {
    const navigate = useNavigate();
    const { activationId } = useParams();
    const id = activationId ? +activationId : 0;
    const { getSharesForActivation } = useShare();
    const { getSurveyResults } = useSurveyResults() as any;
    const { orgId } = useUserOrg();
    const authenticatedApi = useAuthenticatedApi();

    const [shares, setShares] = useState<any[]>([]);
    const [surveyRows, setSurveyRows] = useState<any[]>([]);
    const [status, setStatus] = useState<'all' | 'sent' | 'unsent'>('all');
    const [search, setSearch] = useState('');
    const [preview, setPreview] = useState(
        () => localStorage.getItem(PREVIEW_KEY) !== '0',
    );
    const [limit, setLimit] = useState(PAGE);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        let mounted = true;
        (async () => {
            const raw = (await getSharesForActivation(id).catch(() => [])) ?? [];
            const uniq = dedupeShares(raw);
            if (!mounted) return;
            setShares(uniq);
            setIsLoading(false);
            const sr = await (getSurveyResults?.(id).catch(() => []) ?? []);
            if (mounted) setSurveyRows(sr ?? []);
        })();
        return () => {
            mounted = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    const filtered = useMemo(() => {
        const q = search.toLowerCase();
        return shares
            .filter((s) =>
                status === 'all'
                    ? true
                    : status === 'sent'
                    ? s.sent
                    : !s.sent,
            )
            .filter(
                (s) =>
                    !q || `${s.destination}`.toLowerCase().includes(q),
            )
            .sort(
                (a, b) =>
                    moment(b.createdAt ?? b.created_at).valueOf() -
                    moment(a.createdAt ?? a.created_at).valueOf(),
            );
    }, [shares, status, search]);

    const removeShare = async (s: any) => {
        if (!window.confirm(`Delete the share to ${s.destination}?`)) return;
        const prev = shares;
        setShares((rows) => rows.filter((r) => r.id !== s.id));
        const ok = await authenticatedApi
            .delete(`${orgId}/shares/share/${s.id}`)
            .then(() => true)
            .catch(() => false);
        if (!ok) {
            setShares(prev);
            toast.error("Couldn't delete the share — try again.");
        }
    };

    const exportShares = () =>
        downloadCsv(
            `shares-${id}.csv`,
            shares.map((s) => ({
                destination: s.destination,
                mediaUrl: s.originalUri ?? '',
                type: s.type,
                sent: s.sent ? 'yes' : 'no',
                disclaimerAccepted: s.disclaimerAccepted ?? '',
                createdAt: s.createdAt ?? s.created_at ?? '',
            })),
        );

    const sentCount = shares.filter((s) => s.sent).length;

    if (isLoading) {
        return (
            <main className="mx-auto max-w-[860px] px-[clamp(18px,3vw,34px)] pt-4">
                {[0, 1, 2].map((i) => (
                    <div
                        key={i}
                        className="mb-4 h-12 animate-pulse rounded-[12px] bg-secondary"
                    />
                ))}
            </main>
        );
    }

    return (
        <main className="mx-auto max-w-[860px] px-[clamp(18px,3vw,34px)] pt-4">
            <div className="ps-label mb-4">
                <button
                    type="button"
                    onClick={() => navigate(`/v2/activations/${id}/sharing`)}
                    className="ps-label cursor-pointer border-0 bg-transparent p-0 hover:text-foreground"
                >
                    Sharing
                </button>
                <span className="mx-1.5 opacity-60">/</span>
                <span className="text-muted-foreground">All shares</span>
            </div>

            <div className="mb-4 flex flex-wrap items-center gap-1.5">
                {(
                    [
                        { k: 'all', l: 'All', n: shares.length },
                        { k: 'sent', l: 'Sent', n: sentCount },
                        {
                            k: 'unsent',
                            l: 'Unsent',
                            n: shares.length - sentCount,
                        },
                    ] as const
                ).map((c) => (
                    <button
                        key={c.k}
                        type="button"
                        onClick={() => {
                            setStatus(c.k);
                            setLimit(PAGE);
                        }}
                        className={
                            'cursor-pointer rounded-full px-3 py-[5px] font-[inherit] text-[12px] font-semibold ' +
                            (status === c.k
                                ? 'border border-transparent bg-brand-soft text-brand-ink'
                                : 'border border-border bg-transparent text-muted-foreground')
                        }
                    >
                        {c.l}
                        <span className="ml-1.5 font-normal tabular-nums text-faint">
                            {c.n}
                        </span>
                    </button>
                ))}
                <SearchField
                    className="ml-auto w-[180px]"
                    placeholder="Search destination…"
                    value={search}
                    onChange={(value) => {
                        setSearch(value);
                        setLimit(PAGE);
                    }}
                />
                <button
                    type="button"
                    onClick={exportShares}
                    className="ps-pill cursor-pointer px-3.5 font-[inherit] text-[12px] font-semibold text-foreground"
                >
                    Shares CSV
                </button>
                {surveyRows.length > 0 && (
                    <button
                        type="button"
                        onClick={() =>
                            downloadCsv(`survey-${id}.csv`, surveyRows)
                        }
                        className="ps-pill cursor-pointer px-3.5 font-[inherit] text-[12px] font-semibold text-foreground"
                    >
                        Survey CSV
                    </button>
                )}
            </div>

            <div className="mb-2 flex justify-end">
                <button
                    type="button"
                    onClick={() =>
                        setPreview((p) => {
                            localStorage.setItem(
                                PREVIEW_KEY,
                                p ? '0' : '1',
                            );
                            return !p;
                        })
                    }
                    className="cursor-pointer border-0 bg-transparent p-0 font-[inherit] text-[11.5px] font-semibold text-faint hover:text-foreground"
                >
                    {preview ? 'Hide previews' : 'Show previews'}
                </button>
            </div>

            {filtered.length === 0 && (
                <p className="py-4 text-[13px] text-muted-foreground">
                    {search.trim() || status !== 'all'
                        ? 'No shares match your filters.'
                        : 'No shares yet — they appear here as guests share.'}
                </p>
            )}
            {filtered.slice(0, limit).map((s, i) => (
                <div
                    key={s.id}
                    className={
                        'group flex items-center gap-3 py-2 text-[12.5px] ' +
                        (i > 0 ? 'border-t border-line' : '')
                    }
                >
                    {preview && (
                        <span className="h-[26px] w-[26px] flex-none overflow-hidden rounded-[6px] bg-secondary">
                            {s.thumbnailUri && (
                                <img
                                    src={s.thumbnailUri}
                                    alt=""
                                    loading="lazy"
                                    className="h-full w-full object-cover"
                                />
                            )}
                        </span>
                    )}
                    <span className="min-w-0 flex-1 truncate text-muted-foreground">
                        {s.destination || '—'}
                    </span>
                    <span className="font-mono text-[10px] uppercase text-faint">
                        {s.type === 'text' ? 'SMS' : s.type}
                    </span>
                    <span
                        className={
                            'w-[52px] text-[11px] font-semibold ' +
                            (s.sent ? 'text-good' : 'text-attention')
                        }
                    >
                        {s.sent ? 'sent' : 'unsent'}
                    </span>
                    <span className="w-[86px] whitespace-nowrap text-right text-[11.5px] text-faint">
                        {moment(s.createdAt ?? s.created_at).fromNow(true)}
                    </span>
                    <button
                        type="button"
                        title="Delete share"
                        onClick={() => removeShare(s)}
                        className="cursor-pointer border-0 bg-transparent p-0 text-[13px] text-faint opacity-0 hover:text-attention focus-visible:opacity-100 group-hover:opacity-100"
                    >
                        ×
                    </button>
                </div>
            ))}
            {filtered.length > limit && (
                <div className="flex items-center justify-between border-t border-line py-3">
                    <span className="text-[12px] text-faint">
                        {limit} of {filtered.length}
                    </span>
                    <button
                        type="button"
                        onClick={() => setLimit((l) => l + PAGE)}
                        className="ps-pill cursor-pointer px-4 font-[inherit] text-[12.5px] font-semibold text-foreground"
                    >
                        Show more
                    </button>
                </div>
            )}
        </main>
    );
}
